/* global window URL */

// Origin trust boundary for the embeddable iframe. Kept in its own module so
// both the load/handshake path (loading.js, Startup.jsx) and the snapshot path
// (snapshot.js) share ONE allowlist rather than duplicating the security
// boundary. loading.js re-exports these for backward-compatible imports.

export function isLocalhostUri(uri) {
  try {
    const { hostname } = new URL(uri, window.location.origin);
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  } catch {
    return false;
  }
}

// Origins (scheme + host + port) explicitly trusted to host an EDITABLE inline
// embed off-localhost. This array IS the security boundary: only a parent frame
// whose real handshake origin appears here — and which opts in via
// gridd_editable=1 — is granted an editable Gridd. Adding an entry is a security
// decision; reviewing this list in the PR is the security review. Do not list
// localhost here (it is always trusted for local dev, see isEditableInlineOrigin).
// TODO(maintainer): confirm the exact production origin(s) of trusted hosts.
export const ALLOWED_EDITABLE_ORIGINS = [
  // Slidecraft Studio — UAT
  "https://uat.scs.aerelontech.hu",
  "https://app-uat.griddapp.com",
  // Slidecraft Studio — PROD
  "https://scs.aerelontech.hu",
  "https://app.griddapp.com",
];

// Decide whether an inline embed may be editable, from the real postMessage
// handshake origin (never document.referrer, which a Referrer-Policy can empty
// or trim). Fail-closed: a missing/unknown origin is never trusted. Localhost
// stays editable for local dev without opt-in; every other origin must both be
// explicitly requested via gridd_editable=1 and appear in ALLOWED_EDITABLE_ORIGINS.
export function isEditableInlineOrigin(origin, editableRequested) {
  if (!origin) return false;
  if (isLocalhostUri(origin)) return true;
  return Boolean(editableRequested) && ALLOWED_EDITABLE_ORIGINS.includes(origin);
}

// Decide whether a host frame may pull a snapshot of the rendered grid. A
// snapshot hands the parent the grid's pixels, which the same-origin policy
// would otherwise withhold from a cross-origin embedder — so it is gated to the
// same trusted hosts. Unlike editing, it needs no gridd_editable=1 opt-in (it is
// read-only), but it is still fail-closed: a missing/unknown origin is refused.
export function isTrustedSnapshotOrigin(origin) {
  if (!origin) return false;
  if (isLocalhostUri(origin)) return true;
  return ALLOWED_EDITABLE_ORIGINS.includes(origin);
}
